import * as React from "react";

import CallEndIcon from "@mui/icons-material/CallEnd";
import PhoneIcon from "@mui/icons-material/Phone";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import useTheme from "@mui/material/styles/useTheme";

import { error, loginPromo, misc, neutral, success } from "app/themes/colors";

import { ARTWORK_HEIGHT, artworkHeightSx } from "./login-promo-sizing";

const CHIPS = [
  { label: "$180 due", bg: misc.warningYellow },
  { label: "1 form pending", bg: misc.warningYellow },
  { label: "Phonak Audéo L90 · bilateral", bg: misc.highlightPurple },
  { label: "Next: Thu 2:00 PM", bg: success[100] }
];

const ACTIONS = ["Chart", "Schedule", "Billing"];

/**
 * The incoming-call card from the Suno Voice AI launch post, rebuilt as
 * markup so it stays sharp at any size. It is a picture of the product, so it
 * is one labelled image to assistive tech and none of its parts are
 * interactive. The patient is the launch post's fictional one.
 *
 * Everything inside is sized in `em` off one base font size derived from the
 * card's height, so the whole card scales with the viewport exactly like the
 * Scribes phone screenshot does (1em = 10px at a 342px-tall card).
 */
export default function VoiceCallPreview() {
  const white = useTheme().palette.common.white;
  return (
    <Box
      role="img"
      aria-label="Suno Voice AI showing the caller's patient record during an incoming call"
      display={{ md: "none", lg: "flex" }}
      flexDirection="column"
      flexShrink={0}
      boxSizing="border-box"
      sx={{
        ...artworkHeightSx,
        fontSize: `calc(${ARTWORK_HEIGHT} / 34.2)`,
        // body1's line height is a fixed rem; text here scales in em, so a
        // fixed line height overlaps lines once the card grows.
        "& .MuiTypography-root": { lineHeight: 1.2 },
        width: "24.8em",
        p: "1.2em",
        gap: "0.9em",
        borderRadius: "2.2em",
        bgcolor: loginPromo.callCard,
        color: white,
        boxShadow: "0 24px 60px rgba(0, 0, 0, 0.35)"
      }}
    >
      <Stack direction="row" alignItems="center" gap="0.6em" aria-hidden>
        <Box
          width="0.7em"
          height="0.7em"
          borderRadius="50%"
          bgcolor={loginPromo.voiceTeal}
        />
        <Typography
          component="span"
          fontSize="1.1em"
          fontWeight={700}
          color="inherit"
        >
          Incoming call
        </Typography>
      </Stack>

      <Stack
        aria-hidden
        alignItems="center"
        gap="0.5em"
        sx={{
          bgcolor: white,
          color: neutral[900],
          borderRadius: "1.2em",
          p: "1.2em 1em 1em"
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          width="3.8em"
          height="3.8em"
          borderRadius="50%"
          bgcolor={misc.highlightPurple}
          color={misc.suno}
          fontSize="1em"
          fontWeight={700}
        >
          <Typography component="span" fontSize="1.4em" fontWeight={700}>
            MH
          </Typography>
        </Box>
        <Typography
          component="span"
          fontSize="1.5em"
          fontWeight={700}
          lineHeight={1.2}
        >
          Margaret Henley
        </Typography>
        <Typography
          component="span"
          fontSize="1em"
          color={neutral[700]}
          lineHeight={1.2}
        >
          74 · Patient for 6 years
        </Typography>
        <Stack
          direction="row"
          flexWrap="wrap"
          justifyContent="center"
          gap="0.4em"
        >
          {CHIPS.map(({ label, bg }) => (
            <Box
              key={label}
              component="span"
              sx={{
                fontSize: "0.9em",
                fontWeight: 700,
                lineHeight: 1.5,
                px: "0.7em",
                borderRadius: "999px",
                bgcolor: bg
              }}
            >
              {label}
            </Box>
          ))}
        </Stack>
        <Stack direction="row" gap="0.4em" width="100%" mt="0.3em">
          {ACTIONS.map((label) => (
            <Box
              key={label}
              component="span"
              flex={1}
              textAlign="center"
              sx={{
                fontSize: "0.95em",
                fontWeight: 700,
                lineHeight: 1.6,
                border: `1px solid ${neutral[800]}`,
                borderRadius: "0.6em"
              }}
            >
              {label}
            </Box>
          ))}
        </Stack>
      </Stack>

      <Stack alignItems="center" aria-hidden mt="0.2em">
        <Typography
          component="span"
          fontSize="0.9em"
          letterSpacing="0.12em"
          color={alpha(white, 0.7)}
        >
          INCOMING CALL
        </Typography>
        <Typography component="span" fontSize="1.7em" fontWeight={700}>
          (914) 555-0132
        </Typography>
      </Stack>

      <Stack
        direction="row"
        justifyContent="center"
        gap="3.4em"
        mt="auto"
        aria-hidden
      >
        {[
          { Icon: CallEndIcon, bg: error[600] },
          { Icon: PhoneIcon, bg: success[600] }
        ].map(({ Icon, bg }) => (
          <Box
            key={bg}
            display="flex"
            alignItems="center"
            justifyContent="center"
            width="4em"
            height="4em"
            borderRadius="50%"
            bgcolor={bg}
          >
            <Icon sx={{ fontSize: "1.8em", color: white }} />
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
